import {
  Button,
  Field,
  FieldError,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@/components/ui";
import { useGetPatient } from "@/features/Patient/network/usePatient";
import { isUniversalClinicPatient } from "@/features/Patient/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams } from "@tanstack/react-router";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";
import type { FollowUpResponse } from "../network/useFollowUp";
import { DURATION_UNITS, editFollowUpSchema } from "./schemas";

interface FollowUpEditorProps {
  onSubmit(values: z.infer<typeof editFollowUpSchema>): void;
  onCancel(): void;
  isLoading: boolean;
  followup?: FollowUpResponse;
}

export const FollowUpEditor = ({
  followup,
  onCancel,
  onSubmit,
  isLoading,
}: FollowUpEditorProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { data: patient } = useGetPatient(patientId as string);

  // Universal Clinic (CHAI/Nori) patients receive an AI-sent follow-up, so we
  // leave the manual follow-up message blank rather than pre-filling it.
  const defaultMessage = isUniversalClinicPatient(patient)
    ? ""
    : "Hi, we chatted recently and I would like to see how you are doing. Please message me if you have any questions or concerns.";

  const form = useForm<z.infer<typeof editFollowUpSchema>>({
    resolver: zodResolver(editFollowUpSchema),
    defaultValues: {
      total: followup?.followupDurationValue || 2,
      unit: followup?.followupDurationUnit || "d",
      text: followup?.followupMessage || "",
    },
  });

  // When there is no saved message we pre-fill the default message for the
  // provider. Setting it against an empty default marks the form dirty so the
  // pre-filled content can be saved as-is (empty defaults stay clean/disabled).
  useEffect(() => {
    if (!followup?.followupMessage && defaultMessage) {
      form.setValue("text", defaultMessage, { shouldDirty: true });
    }
  }, [defaultMessage, followup?.followupMessage, form]);

  const textValue = form.watch("text");
  const isSaveDisabled =
    !form.formState.isDirty || textValue.trim().length === 0;

  const handleSubmit = form.handleSubmit((values) =>
    onSubmit({ ...values, text: values.text.trim() }),
  );

  return (
    <form onSubmit={handleSubmit} data-testid="edit-followup-form">
      <div className="flex flex-col gap-2">
        <div className="flex items-start gap-2">
          <Controller
            control={form.control}
            name="total"
            render={({ field, fieldState }) => (
              <Field className="w-1/2" data-invalid={fieldState.invalid}>
                <Input
                  {...field}
                  type="number"
                  data-testid="followup-total"
                  id="total"
                  aria-invalid={fieldState.invalid}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />

          <Controller
            control={form.control}
            name="unit"
            render={({ field, fieldState }) => (
              <Field className="w-1/2" data-invalid={fieldState.invalid}>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={field.disabled}
                >
                  <SelectTrigger
                    data-testid="followup-select"
                    aria-invalid={fieldState.invalid}
                  >
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {DURATION_UNITS.map((unit) => (
                      <SelectItem key={unit.value} value={unit.value}>
                        {unit.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </div>

        <Controller
          control={form.control}
          name="text"
          render={({ field, fieldState }) => (
            <Field className="w-full" data-invalid={fieldState.invalid}>
              <Textarea
                {...field}
                className="h-24"
                data-testid="followup-text"
                autoFocus
                id="text"
                aria-invalid={fieldState.invalid}
              />
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />
      </div>

      <div className="flex gap-2 ml-auto mt-2 justify-end">
        <Button size="xs" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="xs"
          type="submit"
          isLoading={isLoading}
          disabled={isSaveDisabled}
        >
          Save
        </Button>
      </div>
    </form>
  );
};
